import React, { useState } from "react";
import Image from "next/image";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import httpClient from "@/api/http-client";
import endpoints from "@/api/endpoints";
import { phoneRegExp } from "@/utils/regex";
import styles from "../../styles/shap_of_you.module.scss";
import WelcomeScreenImg from "../../assets/images/WelcomeScreen.png";
import SOYLockUp from "../../assets/images/SOYLockUp.png";
import { LoginScreenProps } from "./types";

const LoginScreen: React.FC<LoginScreenProps> = ({
  navigateToStep,
  authData,
  logStep,
}) => {
  const [sendOtpLoading, setSendOtpLoading] = useState(false);

  // Login Formik
  const loginFormik = useFormik({
    initialValues: {
      mobile: authData.mobileNumber || "",
    },
    validationSchema: Yup.object({
      mobile: Yup.string()
        .matches(phoneRegExp, "Mobile number is not valid")
        .test(
          "len",
          "Mobile number needs to be exactly 10 digits",
          (val: any) => val?.toString().length === 10
        )
        .required("Mobile is required"),
    }),
    onSubmit: async (values) => {
      setSendOtpLoading(true);
      const apiEndpoint = endpoints.auth.loginSendOtp();
      const requestPayload = { mobile: values.mobile };

      try {
        const { data } = await httpClient.post(apiEndpoint, requestPayload);

        // Log successful OTP send (non-blocking)
        if (logStep) {
          logStep("signup_otp_sent", {
            api_endpoint: apiEndpoint,
            request_payload: requestPayload,
            response_status: 200,
            response_payload: { otp_uuid: data.otp_uuid },
            success_message: data.message,
          });
        }

        // Success - mobile exists, user can login
        authData.setOtpUid(data.otp_uuid);
        authData.setMobileNumber(values.mobile);
        authData.setAuthFlow("login");

        toast(data.message, {
          type: "success",
          toastId: "auth-otp-success",
        });

        navigateToStep("verifyOtp");
      } catch (error: any) {
        // Log API error (non-blocking)
        if (logStep) {
          logStep("signup_otp_sent", {
            api_endpoint: apiEndpoint,
            request_payload: requestPayload,
            response_status: error.response?.status || 500,
            error_message: error.response?.data?.message || "Failed to send OTP",
            error_code: error.response?.status?.toString() || "500",
          });
        }

        // Check if error is "Mobile number does not exist"
        if (
          error.response?.status === 400 &&
          error.response?.data?.message === "Mobile number does not exist"
        ) {
          // Mobile doesn't exist - navigate to signup
          authData.setMobileNumber(values.mobile);
          authData.setAuthFlow("register");
          authData.setRegisterData({
            ...authData.registerData,
            mobile: values.mobile,
          });

          toast("Mobile number not registered. Please sign up.", {
            type: "info",
            toastId: "auth-signup-required",
          });

          navigateToStep("signup");
        } else {
          // Other errors
          if (error.response?.data?.message) {
            toast(error.response.data.message, {
              type: "error",
              toastId: "auth-otp-error",
            });
          } else {
            toast("Something went wrong! Try again!", {
              type: "error",
              toastId: "auth-otp-error",
            });
          }
        }
      }
      setSendOtpLoading(false);
    },
  });

  return (
    <div className="d-md-none w-100">
      <div className={styles.loginWrapper}>
        {/* Background Section */}
        <div className={styles.bgImage}>
          <Image src={WelcomeScreenImg} alt="Welcome Screen" />
        </div>
        <div className={styles.hero}>
          <div className={styles.logo}>
            <Image src={SOYLockUp} alt="Shape of You Logo" />
          </div>
        </div>

        {/* Login Card */}
        <div className={styles.card}>
          <h2>Login</h2>
          <p>Enter your phone number</p>

          <form onSubmit={loginFormik.handleSubmit}>
            <div className={styles.inputBox}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5 4H9L11 9L8.5 10.5C9.57096 12.6715 11.3285 14.429 13.5 15.5L15 13L20 15V19C20 19.5304 19.7893 20.0391 19.4142 20.4142C19.0391 20.7893 18.5304 21 18 21C14.0993 20.763 10.4202 19.1065 7.65683 16.3432C4.8935 13.5798 3.23705 9.90074 3 6C3 5.46957 3.21071 4.96086 3.58579 4.58579C3.96086 4.21071 4.46957 4 5 4Z"
                  stroke="#262121"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>

              <input
                type="tel"
                placeholder="Phone Number"
                maxLength={10}
                value={loginFormik.values.mobile}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  loginFormik.setFieldValue("mobile", value);
                }}
                onBlur={loginFormik.handleBlur}
              />
            </div>

            {loginFormik.touched.mobile && loginFormik.errors.mobile ? (
              <small
                className="text-danger"
                style={{ display: "block", marginTop: "5px", textAlign: "center" }}
              >
                {loginFormik.errors.mobile}
              </small>
            ) : null}

            <button
              className={styles.btn}
              type="submit"
              disabled={
                sendOtpLoading ||
                !loginFormik.isValid ||
                !loginFormik.values.mobile ||
                loginFormik.values.mobile.length !== 10
              }
              style={
                sendOtpLoading ||
                !loginFormik.isValid ||
                !loginFormik.values.mobile ||
                loginFormik.values.mobile.length !== 10
                  ? {
                      background: "#d3d3d3",
                      color: "#888",
                      cursor: "not-allowed",
                      opacity: 0.6,
                    }
                  : {
                      background: "#C9302C",
                      color: "white",
                      cursor: "pointer",
                    }
              }
            >
              {sendOtpLoading ? "Sending..." : "Proceed"} <span>→</span>
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
